import * as Yup from "yup";
import { validation, conditionalValidation } from "../../../utils/constants";

export const XML_FILE_TYPES = ["XML", "XML_HLBC_PHMS"];
export const SINGLE_XML_FORMAT_MESSAGE = "Only one XML format can be selected at a time.";

const fileTypeSchema = Yup.array()
    .of(Yup.string())
    .min(1, "File Type is a required field")
    .test("single-xml", SINGLE_XML_FORMAT_MESSAGE, (vals) => {
        if (!vals?.length) return true;
        return vals.filter((v) => XML_FILE_TYPES.includes(v)).length <= 1;
    });

/** Keep at most one XML format; prefer the XML type the user just added. */
export const resolveFileTypeSelection = (previous = [], selected = []) => {
    const next = selected.map((v) => v.value);
    const xmlInNext = next.filter((v) => XML_FILE_TYPES.includes(v));
    if (xmlInNext.length <= 1) {
        return { fileType: next, rejected: false };
    }
    const prevXml = previous.filter((v) => XML_FILE_TYPES.includes(v));
    const addedXml = xmlInNext.filter((v) => !prevXml.includes(v));
    const keepXml = addedXml.length ? addedXml[addedXml.length - 1] : xmlInNext[xmlInNext.length - 1];
    return {
        fileType: [...next.filter((v) => !XML_FILE_TYPES.includes(v)), keepXml],
        rejected: true,
    };
};

const protocolRequired = Yup.string().when("type", {
    is: (val) => val === "MFT" || val === "HL7",
    then: (schema) => schema.required("Protocol is Required"),
    otherwise: (schema) => schema.notRequired(),
});

const hostNameRequired = Yup.string().when("type", {
    is: (val) => val === "MFT" || val === "HL7",
    then: (schema) => schema.required("Hostname is Required"),
    otherwise: (schema) => schema.notRequired(),
});

const targetRequired = Yup.string().when("type", {
    is: (val) => val !== "HL7",
    then: (schema) => schema.required("Delivery Target is a required field"),
    otherwise: (schema) => schema.notRequired(),
});

const hl7TemplateRequired = Yup.string().when("type", {
    is: "HL7",
    then: (schema) => schema.required("HL7 template is required"),
    otherwise: (schema) => schema.notRequired(),
});

const hl7CertRequired = (fieldLabel) =>
    Yup.string().when("type", {
        is: "HL7",
        then: (schema) => schema.required(`${fieldLabel} is required`),
        otherwise: (schema) => schema.notRequired(),
    });

const sharedValidationSchema = {
    orgId: conditionalValidation(
        Yup.string(),
        {
            validation,
            label: "Organization",
            mandatory: true
        }),
    name: conditionalValidation(
        Yup.string(),
        {
            validation,
            label: "Report Delivery Name",
            mandatory: true
        }),
    type: conditionalValidation(
        Yup.string(),
        {
            validation,
            label: "Report Delivery Type",
            mandatory: true
        }),
    protocol: conditionalValidation(protocolRequired, {
        validation,
        label: "Protocol"
    }),
    hostName: conditionalValidation(hostNameRequired, {
        validation,
        label: "Hostname"
    }),
    template: conditionalValidation(hl7TemplateRequired, {
        validation,
        label: "HL7 Template"
    }),
    localCertificateArn: conditionalValidation(hl7CertRequired("Local certificate ARN"), {
        validation,
        label: "Local certificate ARN"
    }),
    remoteCertificateArn: conditionalValidation(hl7CertRequired("Remote certificate ARN"), {
        validation,
        label: "Remote certificate ARN"
    }),
    fileType: fileTypeSchema,
    target: conditionalValidation(targetRequired, {
        validation,
        label: "Delivery Target",
        mandatory: false
    }),
    userName: conditionalValidation(
        Yup.string(),
        {
            validation,
            label: "Username",
        })
};

export const FormikObj = {
    initialValues: {
        id: "",
        orgId: "",
        name: "",
        type: "",
        fileType: ["PDF"],
        protocol: "",
        hostName: "",
        userName: "",
        secret: "",
        target: "",
        messageSpec: "",
        template: "",
        localCertificateArn: "",
        remoteCertificateArn: "",
    },
    validationSchema: Yup.object(sharedValidationSchema),
}

export const FormikObjEdit = {
    initialValues: {
        id: "",
        orgId: "",
        name: "",
        type: "",
        fileType: [],
        protocol: "",
        hostName: "",
        userName: "",
        secret: "",
        target: "",
        messageSpec: "",
        template: "",
        localCertificateArn: "",
        remoteCertificateArn: "",
    },
    validationSchema: Yup.object(sharedValidationSchema),
}

export const fileTypes = [
    { value: "PDF", label: "PDF" },
    { value: "XML", label: "Florizel XML" },
    { value: "XML_HLBC_PHMS", label: "HLBC PHMS Legacy XML" },
]

/** Fixed fileType sent for HL7 V2 registries (Step Function gate); not shown in the admin multi-select. */
export const hl7FileTypes = ["HL7"];

export const deliveryTypes = [
    { value: "FAX", label: "Fax" },
    { value: "MFT", label: "MFT" },
    { value: "HL7", label: "HL7 V2" },
]

export const protocols = [
    { value: 'SFTP', label: "SFTP"},
    { value: 'HTTPS', label: 'HTTPS'}
]
export const hl7Protocols = [
    { value: "MLLP", label: "MLLP" },
]
export const hl7Templates = [
    { value: "hl7/oru/default.hbs", label: "hl7/oru/default.hbs" },
]
